import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as userManagementApi from '@/api/user-management.api';

export const userManagementKeys = {
  all: ['user-management'] as const,
  groups: () => [...userManagementKeys.all, 'groups'] as const,
  details: (userId: string) => [...userManagementKeys.all, 'details', userId] as const,
};

/**
 * Get all LDAP groups
 */
export function useLdapGroups() {
  return useQuery({
    queryKey: userManagementKeys.groups(),
    queryFn: () => userManagementApi.getLdapGroups(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get user management details (groups, manager)
 */
export function useUserManagementDetails(userId: string | undefined) {
  return useQuery({
    queryKey: userManagementKeys.details(userId || ''),
    queryFn: () => userManagementApi.getUserManagementDetails(userId!),
    enabled: !!userId,
    staleTime: 1000 * 30, // 30 seconds
  });
}

/**
 * Deactivate user mutation
 */
export function useDeactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => userManagementApi.deactivateUser(userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: userManagementKeys.details(userId) });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/**
 * Activate user mutation
 */
export function useActivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => userManagementApi.activateUser(userId),
    onSuccess: (_, userId) => {
      queryClient.invalidateQueries({ queryKey: userManagementKeys.details(userId) });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/**
 * Change password mutation
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: ({ userId, newPassword }: { userId: string; newPassword: string }) =>
      userManagementApi.changePassword(userId, newPassword),
  });
}

/**
 * Change email mutation (LDAP and the app)
 */
export function useUpdateEmail() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, email }: { userId: string; email: string }) => userManagementApi.updateEmail(userId, email),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: userManagementKeys.details(userId) });
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

/**
 * Reset password mutation
 */
export function useResetPassword() {
  return useMutation({
    mutationFn: (userId: string) => userManagementApi.resetPassword(userId),
  });
}

/**
 * Add user to group mutation
 */
export function useAddUserToGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, groupDn }: { userId: string; groupDn: string }) =>
      userManagementApi.addUserToGroup(userId, groupDn),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: userManagementKeys.details(userId) });
    },
  });
}

/**
 * Remove user from group mutation
 */
export function useRemoveUserFromGroup() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, groupDn }: { userId: string; groupDn: string }) =>
      userManagementApi.removeUserFromGroup(userId, groupDn),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: userManagementKeys.details(userId) });
    },
  });
}

// Re-export types
export type { LdapGroup, LdapManager, UserManagementDetails, PasswordResetResult } from '@/api/user-management.api';
